import React from 'react';
import PropTypes from 'prop-types';
import {
  LuBell,
  LuChevronDown,
  LuChevronsLeft,
  LuCircleUserRound,
  LuHouse,
  LuLayoutGrid,
  LuListTodo,
  LuLogOut,
  LuMail,
  LuMoon,
  LuSearch,
} from 'react-icons/lu';
import logoMark from '../../../../assets/logo_min.png';
import { useTranslation } from '../../../../i18n/LanguageContext';
import { abs } from '../engine/stage';
import { FRAME } from '../layout';
import { Avatar, GLASS_CHIP, GLASS_PANEL, GLASS_POPOVER, IconBtn } from './kit';

const MenuItem = ({ rect, icon: Icon, label, active, chevron, open, small, hover }) => (
  <span
    style={abs(rect)}
    className={`tw-absolute tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-px-2 tw-text-[14px] tw-font-medium tw-transition-colors tw-duration-300 ${
      active ? 'tw-bg-teal-500/10 tw-text-slate-900 dark:tw-text-white' : hover ? 'tw-bg-slate-900/[0.04] tw-text-slate-800 dark:tw-bg-white/[0.05] dark:tw-text-slate-100' : 'tw-text-slate-700 dark:tw-text-slate-300'
    }`}
  >
    {small ? (
      <span className={`tw-ml-3 tw-h-1.5 tw-w-1.5 tw-rounded-full ${active ? 'tw-bg-teal-500' : 'tw-bg-slate-300 dark:tw-bg-white/25'}`} />
    ) : (
      <span className={`tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-xl ${active ? 'tw-bg-gradient-to-br tw-from-teal-400 tw-to-cyan-600 tw-text-white tw-shadow-md tw-shadow-teal-500/30' : `${GLASS_CHIP} tw-text-slate-500 dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none dark:tw-text-slate-300`}`}>
        <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
      </span>
    )}
    <span className="tw-min-w-0 tw-flex-1 tw-truncate">{label}</span>
    {chevron && <LuChevronDown aria-hidden="true" className={`tw-mr-1 tw-h-4 tw-w-4 tw-text-slate-400 tw-transition-transform tw-duration-300 ${open ? 'tw-rotate-180' : ''}`} />}
  </span>
);

MenuItem.propTypes = { rect: PropTypes.object.isRequired, icon: PropTypes.elementType, label: PropTypes.node, active: PropTypes.bool, chevron: PropTypes.bool, open: PropTypes.bool, small: PropTypes.bool, hover: PropTypes.bool };

/**
 * The portal shell as a regular employee sees it: sidebar (Ana Sayfa with
 * its Yapılacaklar Listesi submenu, Profil, Hall and Çıkış Yap) and the top
 * bar (title, menu search, Hall / Ana Sayfa, Görevlerim bell, TR|EN, theme,
 * user menu). The page is drawn inside as children.
 *
 * frame state: { page: 'dashboard'|'profile', homeOpen, hover, searchOpen,
 * searchText, userMenu, unseenTasks, unreadMessages, collapsedHint }
 */
const AppFrame = ({ frame = {}, children }) => {
  const { t } = useTranslation();
  const page = frame.page || 'dashboard';
  const title = page === 'profile' ? t('shell.profile') : t('shell.home');
  const profileRect = frame.homeOpen ? FRAME.navProfileOpen : FRAME.navProfile;
  const query = frame.searchText || '';
  const results = [
    { key: 'home', icon: LuLayoutGrid, label: t('shell.home'), group: t('shell.home') },
    { key: 'todo', icon: LuListTodo, label: t('dashboard.tasks.title'), group: t('shell.home') },
    { key: 'profile', icon: LuCircleUserRound, label: t('shell.profile'), group: t('shell.profile') },
  ].filter((item) => !query || item.label.toLocaleLowerCase('tr-TR').includes(query.toLocaleLowerCase('tr-TR')));

  return (
    <div className="tw-absolute tw-inset-0 tw-overflow-hidden tw-bg-[#eef1f5] dark:tw-bg-[#16181d]">
      {/* Page */}
      <div className="tw-absolute tw-inset-0">{children}</div>

      {/* Sidebar */}
      <div style={abs(FRAME.sidebar)} className={`tw-absolute tw-z-[6] tw-rounded-[1.75rem] ${GLASS_PANEL}`}>
        <div className="tw-flex tw-items-center tw-gap-3 tw-px-5 tw-pt-5">
          <span className="tw-grid tw-h-10 tw-w-12 tw-place-items-center tw-overflow-hidden tw-rounded-lg tw-bg-white">
            <img src={logoMark} alt="" className="tw-h-9 tw-w-auto" />
          </span>
          <span className="tw-min-w-0">
            <span className="tw-block tw-text-[15px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">HabcareCRO</span>
            <span className="tw-block tw-text-[10px] tw-font-semibold tw-uppercase tw-tracking-[0.3em] tw-text-slate-400">{t('shell.portal')}</span>
          </span>
        </div>
        <span className="tw-absolute tw-inset-x-3 tw-bottom-[104px] tw-h-px tw-bg-slate-200/80 dark:tw-bg-white/10" />
      </div>
      <span style={abs(FRAME.collapse)} className={`tw-absolute tw-z-[7] tw-grid tw-place-items-center tw-rounded-full tw-text-slate-500 ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-[#262a34] dark:tw-bg-none`}>
        <LuChevronsLeft aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      </span>
      <div className="tw-absolute tw-inset-0 tw-z-[7]">
        <MenuItem rect={FRAME.navHome} icon={LuLayoutGrid} label={t('shell.home')} active={page === 'dashboard'} chevron open={frame.homeOpen} hover={frame.hover === 'home'} />
        {frame.homeOpen && <MenuItem rect={FRAME.navTodo} label={t('dashboard.tasks.title')} small hover={frame.hover === 'todo'} />}
        <MenuItem rect={profileRect} icon={LuCircleUserRound} label={t('shell.profile')} active={page === 'profile'} hover={frame.hover === 'profile'} />
        <MenuItem rect={FRAME.hall} icon={LuLayoutGrid} label={t('shell.hall')} />
        <span style={abs(FRAME.signOut)} className="tw-absolute tw-flex tw-items-center tw-gap-3 tw-px-2 tw-text-[14px] tw-font-medium tw-text-rose-600 dark:tw-text-rose-300">
          <span className={`tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-xl ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none`}>
            <LuLogOut aria-hidden="true" className="tw-h-4 tw-w-4" />
          </span>
          {t('shell.signOut')}
        </span>
      </div>

      {/* Top bar */}
      <div style={abs(FRAME.topbar)} className={`tw-absolute tw-z-[6] tw-rounded-[1.4rem] ${GLASS_PANEL}`} />
      <div className="tw-absolute tw-inset-0 tw-z-[7]">
        <span style={{ left: 260, top: 25 }} className="tw-absolute tw-text-[17px] tw-font-semibold tw-text-slate-900 dark:tw-text-white">{title}</span>
        <span style={abs(FRAME.search)} className={`tw-absolute tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-px-3.5 tw-text-sm ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none ${frame.searchOpen ? 'tw-ring-2 tw-ring-teal-500/40' : ''}`}>
          <LuSearch aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
          <span className={`tw-flex-1 tw-truncate ${query ? 'tw-text-slate-800 dark:tw-text-slate-100' : 'tw-text-slate-400'}`}>
            {query || t('topbar.search.placeholder')}
            {frame.searchOpen && <span className="tw-ml-px tw-inline-block tw-h-4 tw-w-px tw-translate-y-0.5 tw-animate-pulse tw-bg-teal-600" />}
          </span>
          <span className="tw-rounded-md tw-border tw-border-slate-200 tw-px-1.5 tw-text-[10px] tw-font-semibold tw-text-slate-400 dark:tw-border-white/10">Ctrl K</span>
        </span>
        {frame.searchOpen && (
          <div style={{ ...abs(FRAME.searchResults), height: 'auto' }} className={`tw-absolute tw-rounded-2xl tw-p-1.5 ${GLASS_POPOVER}`}>
            {results.map((item, index) => {
              const Icon = item.icon;
              return (
                <span key={item.key} className={`tw-flex tw-h-10 tw-items-center tw-gap-3 tw-rounded-xl tw-px-2.5 ${index === 0 ? 'tw-bg-teal-500/10' : ''}`}>
                  <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-teal-600 dark:tw-text-teal-300" />
                  <span className="tw-flex-1 tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{item.label}</span>
                  <span className="tw-text-[11px] tw-text-slate-400">{item.group}</span>
                </span>
              );
            })}
          </div>
        )}
        <IconBtn rect={FRAME.hallBtn} icon={LuLayoutGrid} />
        <IconBtn rect={FRAME.homeBtn} icon={LuHouse} />
        {frame.unreadMessages ? <IconBtn rect={{ ...FRAME.hallBtn, x: FRAME.hallBtn.x - 42 }} icon={LuMail} badge={frame.unreadMessages} tone="tw-text-rose-500" /> : null}
        <IconBtn rect={FRAME.bell} icon={LuBell} badge={frame.unseenTasks} active={Boolean(frame.unseenTasks)} />
        <span style={abs(FRAME.lang)} className="tw-absolute tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-slate-900/[0.05] tw-p-1 tw-text-xs tw-font-semibold dark:tw-bg-white/[0.06]">
          <span className="tw-grid tw-h-full tw-flex-1 tw-place-items-center tw-rounded-full tw-bg-white tw-text-slate-900 tw-shadow-sm dark:tw-bg-white/15 dark:tw-text-white">TR</span>
          <span className="tw-grid tw-h-full tw-flex-1 tw-place-items-center tw-text-slate-500">EN</span>
        </span>
        <IconBtn rect={FRAME.theme} icon={LuMoon} />
        <span style={abs(FRAME.user)} className={`tw-absolute tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-pl-1 tw-pr-2.5 ${GLASS_CHIP} dark:tw-border-white/10 dark:tw-bg-white/[0.06] dark:tw-bg-none ${frame.userMenu ? 'tw-ring-2 tw-ring-teal-500/40' : ''}`}>
          <Avatar initials="AK" size={28} />
          <span className="tw-flex-1 tw-truncate tw-text-[13px] tw-font-semibold tw-text-slate-800 dark:tw-text-slate-100">Ayşe Kaya</span>
          <LuChevronDown aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-text-slate-400" />
        </span>
        {frame.userMenu && (
          <div style={abs(FRAME.userMenu)} className={`tw-absolute tw-rounded-2xl tw-p-3 ${GLASS_POPOVER}`}>
            <div className="tw-flex tw-items-center tw-gap-3 tw-px-1 tw-pb-3">
              <Avatar initials="AK" size={44} />
              <span className="tw-min-w-0">
                <span className="tw-block tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">Ayşe Kaya</span>
                <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500">ayse.kaya@habcarecro.com</span>
              </span>
            </div>
            <span style={{ left: FRAME.userMenuProfile.x - FRAME.userMenu.x, top: FRAME.userMenuProfile.y - FRAME.userMenu.y, width: FRAME.userMenuProfile.w, height: FRAME.userMenuProfile.h }} className={`tw-absolute tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-px-2.5 tw-text-sm tw-font-medium tw-text-slate-700 dark:tw-text-slate-200 ${frame.userMenu === 'profile' ? 'tw-bg-teal-500/10' : ''}`}>
              <LuCircleUserRound aria-hidden="true" className="tw-h-4 tw-w-4" />
              {t('shell.profile')}
            </span>
            <span style={{ left: 12, right: 12, top: FRAME.userMenuSignOut.y - FRAME.userMenu.y - 9 }} className="tw-absolute tw-h-px tw-bg-slate-200/80 dark:tw-bg-white/10" />
            <span style={{ left: FRAME.userMenuSignOut.x - FRAME.userMenu.x, top: FRAME.userMenuSignOut.y - FRAME.userMenu.y, width: FRAME.userMenuSignOut.w, height: FRAME.userMenuSignOut.h }} className="tw-absolute tw-flex tw-items-center tw-gap-2.5 tw-rounded-xl tw-px-2.5 tw-text-sm tw-font-medium tw-text-rose-600 dark:tw-text-rose-300">
              <LuLogOut aria-hidden="true" className="tw-h-4 tw-w-4" />
              {t('shell.signOut')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

AppFrame.propTypes = { frame: PropTypes.object, children: PropTypes.node };

export default AppFrame;
